<script setup lang="ts">
import { faWallet } from '@fortawesome/pro-regular-svg-icons';
import AutoReserveCard from './_components/AutoReserveCard.vue';
import EmptyState from '@components/ui/auto-reserve&trackings/EmptyState.vue';
import PanelPageContainer from '@components/layout/org&panel&support/PanelPageContainer.vue';
import { type AutomaticReserveDemandResponseExtended } from '@store/auto-reserve';

import TabBar from '@components/util/TabBar.vue';

definePageMeta({
    scrollToTop: true,
});

const page = ref(1);
const currentTab = ref(0);

const hasNextPage = computed(
    () =>
        !isLoadingInitially.value &&
        autoReserveStore.demands.length < autoReserveStore.demandsCount
);
const autoReserveStore = useAutoReserveStore();
const authStore = useAuthStore();
const { credit } = storeToRefs(authStore);

const isLoadingInitially = ref(false);
const isLoading = ref(false);

const autoReserveDemands = computed<AutomaticReserveDemandResponseExtended[]>(
    () => {
        if (isLoadingInitially.value) {
            return [
                { packages: [] },
                { packages: [] },
                { packages: [] },
            ] as AutomaticReserveDemandResponseExtended[];
        }
        return autoReserveStore.autoReserveNormalizedDemands as AutomaticReserveDemandResponseExtended[];
    }
);

const creditAmount = computed(() => formatMoney(credit.value));

async function refresh() {
    autoReserveStore.demands = [];
    isLoadingInitially.value = true;
    page.value = 1;
    await autoReserveStore.getAutoReserveDemands(
        page.value,
        currentTab.value === 0
    );
    isLoadingInitially.value = false;
}

onMounted(() => {
    authStore.loadCredit(true);
    refresh();
    autoReserveStore.getCancellationReasons();
});

watch(page, async value => {
    isLoading.value = true;
    await autoReserveStore.getAutoReserveDemands(value, currentTab.value === 0);
    isLoading.value = false;
});
watch(currentTab, () => refresh());
</script>

<template>
    <div class="root-container">
        <PanelPageContainer
            title="مدیریت رزرو خودکار"
            class="panel-container"
            retain-box-in-mobile
        >
            <div class="credit-container">
                <div class="credit-value-container">
                    <div class="credit-icon-container">
                        <mr-icon
                            :icon="faWallet"
                            size="x-large"
                            color="var(--blue-400)"
                        />
                    </div>
                    <div class="credit-value">
                        <mr-typography
                            size="large"
                            weight="500"
                            color="var(--cool-gray-700)"
                        >
                            اعتبار کاربری
                        </mr-typography>
                        <mr-typography
                            size="display4"
                            weight="700"
                            color="var(--blue-500)"
                        >
                            {{ creditAmount }}
                        </mr-typography>
                    </div>
                </div>
                <mr-divider class="divider" thickness="1px" vertical />
                <mr-button
                    href="/panel/credit"
                    link="nuxt"
                    class="credit-button"
                >
                    افزایش و استرداد اعتبار
                </mr-button>
            </div>
            <div class="messages-container">
                <mr-flash-message
                    :items="[
                        {
                            message:
                                'رزرو خودکار مختص سفرهایی با ظرفیت تکمیل شده است، در صورت نبود کنسلی، رزرو خودکار شما با موفقیت انجام نخواهد شد.',
                        },
                        {
                            message:
                                'تا ۳ ساعت پیش از حرکت سفرهای انتخابی، رهگیری رزرو خودکار ادامه خواهد داشت و امکان رزرو موفق وجود خواهد داشت.',
                        },
                        {
                            message:
                                'اعتبار موجود در حساب کاربری شما همواره قابل استفاده و قابل استرداد خواهد بود، اما در صورتی که رزرو خودکار فعالی داشته باشید و اعتبار لازم در حساب شما موجود نباشد، رزرو خودکار شما ناموفق خواهد شد.',
                        },
                    ]"
                />
                <mr-flash-message
                    message="به منظور انصراف از رزرو خودکار، گزینه حذف رزرو را روی کارت پرواز مورد نظر را انتخاب کنید."
                    type="warning"
                />
            </div>
        </PanelPageContainer>
        <PanelPageContainer disable-title-portal retain-box-in-mobile>
            <TabBar
                v-model="currentTab"
                :tabs="[{ title: 'جاری' }, { title: 'گذشته' }]"
            />
            <mr-infinite-scroll
                v-if="autoReserveDemands.length"
                class="list-container"
                :items="autoReserveDemands"
                :has-next="hasNextPage"
                @load="page++"
            >
                <template #item="{ item }">
                    <AutoReserveCard
                        :key="item.id"
                        :item
                        :loading="isLoadingInitially"
                    />
                </template>
            </mr-infinite-scroll>
            <EmptyState v-else mode="autoReserve" />
            <mr-loading v-if="isLoading" />
        </PanelPageContainer>
    </div>
</template>
<style lang="scss" scoped>
:deep(.split-line) {
    margin-block: 0 !important;
    width: 100%;
}

.root-container {
    .panel-container {
        display: flex;
        flex-direction: column;
        gap: 20px;
        .credit-container {
            display: flex;
            gap: 20px;
            align-items: center;
            @include mobile-only {
                flex-direction: column;
                align-items: flex-start;
            }

            .credit-value-container {
                display: flex;
                gap: 20px;

                .credit-icon-container {
                    padding: 10px;
                    border-radius: 20px;
                    background-color: usePalette($p-blue, $p-100);
                }

                .credit-value {
                    display: flex;
                    flex-direction: column;
                }
            }

            .divider {
                height: 68px;
                @include mobile-only {
                    display: none;
                }
            }

            .credit-button {
                @include mobile-only {
                    width: 100%;
                }
            }
        }

        .messages-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
        }
    }
    .list-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
}
</style>
